import {
  db,
  getSetting,
  saveSetting,
  getAllMachines,
  getAllTemplates,
  getAllInspectors,
  INSPECTORS_SETTING_KEY,
} from '../../db';
import { EMBEDDED_SYSTEM_CONFIG, type EmbeddedSystemConfig } from '../../config/embeddedConfig';
import { saveLineAlertConfig, type LineAlertConfig } from '../line/lineAlertDispatcher';
import type { Machine, ChecklistTemplate, Inspector } from '../../types';

export interface CloudSystemConfig {
  gas_endpoint_url?: string;
  gas_config?: {
    key?: string;
    webAppUrl?: string;
    enabled?: boolean;
    autoSync?: boolean;
    updatedAt?: string;
  };
  line_alert_config?: LineAlertConfig;
  machines?: Machine[];
  templates?: ChecklistTemplate[];
  inspectors?: Inspector[];
  systemDataUpdatedAt?: string;
  version?: string;
  updatedAt?: string;
}

export interface BroadcastResult {
  localSaved: boolean;
  serverSaved: boolean;
  cloudSaved: boolean;
  message: string;
}

export type SystemDataUpdateListener = () => void;
const updateListeners: Set<SystemDataUpdateListener> = new Set();

/**
 * Subscribe to system data updates (machines, templates, inspectors)
 * React components like App.tsx or DashboardView can listen and re-render immediately
 */
export function subscribeSystemDataUpdates(listener: SystemDataUpdateListener): () => void {
  updateListeners.add(listener);
  return () => {
    updateListeners.delete(listener);
  };
}

function notifySystemDataUpdates(): void {
  for (const listener of updateListeners) {
    try {
      listener();
    } catch (err) {
      console.error('[cloudConfigService] Update listener error:', err);
    }
  }
}

/**
 * Initialize system settings on app boot:
 * 1. Checks URL query params for 1-Click setup (?setup=true)
 * 2. Seeds IndexedDB with EMBEDDED_SYSTEM_CONFIG if local DB is empty (Zero-Config guarantee)
 * 3. Checks server /api/app-config or public/app-config.json for LAN updates (Machines, Templates, Inspectors)
 * 4. Checks Google Sheets System_Config tab for cloud updates
 */
export async function initSystemConfig(): Promise<{
  source: 'url_setup' | 'cloud' | 'server' | 'local' | 'embedded';
  updated: boolean;
}> {
  try {
    // 1. Check for URL 1-Click Setup Parameters (e.g. ?setup=true&gas=... or ?config=base64)
    if (typeof window !== 'undefined' && window.location.search) {
      const urlParams = new URLSearchParams(window.location.search);
      const isSetup = urlParams.get('setup') === 'true';
      const gasParam = urlParams.get('gas');
      const configParam = urlParams.get('config');

      if (isSetup || gasParam || configParam) {
        let applied = false;

        if (configParam) {
          try {
            const decoded = JSON.parse(decodeURIComponent(escape(atob(configParam))));
            await applyConfigToLocal(decoded);
            applied = true;
          } catch (e) {
            console.warn('[cloudConfigService] Failed to parse encoded config URL param:', e);
          }
        }

        if (!applied && gasParam) {
          const trimmedGas = decodeURIComponent(gasParam).trim();
          if (trimmedGas.startsWith('http')) {
            await saveSetting('gas_endpoint_url', trimmedGas);
            await saveSetting('gas_config', {
              key: 'gas_config',
              webAppUrl: trimmedGas,
              enabled: true,
              autoSync: true,
              updatedAt: new Date().toISOString(),
            });
            applied = true;
          }
        }

        if (applied) {
          // Clean URL without reloading page
          try {
            const cleanUrl = window.location.origin + window.location.pathname + window.location.hash;
            window.history.replaceState({}, document.title, cleanUrl);
          } catch (_) {}
          return { source: 'url_setup', updated: true };
        }
      }
    }

    // 2. Read existing local settings from IndexedDB
    const existingUrl = await getSetting<string>('gas_endpoint_url');
    const existingConfig = await getSetting<{ updatedAt?: string; webAppUrl?: string }>('gas_config');

    let isFreshInstall = !existingUrl || !existingConfig?.webAppUrl;

    // If local storage is fresh/empty, seed immediately with EMBEDDED_SYSTEM_CONFIG
    if (isFreshInstall) {
      console.log('[cloudConfigService] Seeding local database with EMBEDDED_SYSTEM_CONFIG');
      await applyConfigToLocal(EMBEDDED_SYSTEM_CONFIG);
    }

    // 3. If online, check server configuration (/api/app-config or /app-config.json)
    if (typeof navigator !== 'undefined' && navigator.onLine) {
      const serverConfig = await fetchServerConfig();
      if (serverConfig) {
        const localTime = new Date(
          (await getSetting<string>('system_data_updated_at')) ||
          existingConfig?.updatedAt ||
          EMBEDDED_SYSTEM_CONFIG.updatedAt
        ).getTime();

        const serverTime = new Date(serverConfig.systemDataUpdatedAt || serverConfig.updatedAt || 0).getTime();

        if (serverTime > localTime) {
          console.log('[cloudConfigService] Applying newer server configuration & entities from LAN host');
          await applyConfigToLocal(serverConfig);
          return { source: 'server', updated: true };
        }
      }

      // 4. Check Google Sheets Cloud Config (if URL is available)
      const currentGasUrl = (await getSetting<string>('gas_endpoint_url')) || EMBEDDED_SYSTEM_CONFIG.gas_endpoint_url;
      if (currentGasUrl && currentGasUrl.startsWith('http')) {
        const cloudResult = await fetchCloudConfig(currentGasUrl);
        if (cloudResult.success && cloudResult.config) {
          const localTime = new Date(
            (await getSetting<string>('system_data_updated_at')) ||
            existingConfig?.updatedAt ||
            EMBEDDED_SYSTEM_CONFIG.updatedAt
          ).getTime();

          const cloudTime = new Date(
            cloudResult.config.systemDataUpdatedAt || cloudResult.config.updatedAt || 0
          ).getTime();

          if (cloudTime > localTime) {
            console.log('[cloudConfigService] Applying newer cloud configuration & entities from Google Sheets');
            await applyConfigToLocal(cloudResult.config);
            return { source: 'cloud', updated: true };
          }
        }
      }
    }

    return {
      source: isFreshInstall ? 'embedded' : 'local',
      updated: isFreshInstall,
    };
  } catch (err) {
    console.error('[cloudConfigService] Error during initSystemConfig:', err);
    return { source: 'embedded', updated: false };
  }
}

/**
 * Applies a given config object into local IndexedDB:
 * - Endpoints & Alert Config
 * - Machines
 * - Templates
 * - Inspectors
 */
export async function applyConfigToLocal(config: CloudSystemConfig): Promise<void> {
  const now = config.systemDataUpdatedAt || config.updatedAt || new Date().toISOString();

  // 1. Google Sheets Settings
  if (config.gas_endpoint_url) {
    await saveSetting('gas_endpoint_url', config.gas_endpoint_url);
  }

  if (config.gas_config) {
    await saveSetting('gas_config', {
      key: 'gas_config',
      webAppUrl: config.gas_config.webAppUrl || config.gas_endpoint_url || '',
      enabled: config.gas_config.enabled !== false,
      autoSync: config.gas_config.autoSync !== false,
      updatedAt: now,
    });
  } else if (config.gas_endpoint_url) {
    await saveSetting('gas_config', {
      key: 'gas_config',
      webAppUrl: config.gas_endpoint_url,
      enabled: true,
      autoSync: true,
      updatedAt: now,
    });
  }

  // 2. LINE Alert Settings
  if (config.line_alert_config) {
    await saveLineAlertConfig({
      ...config.line_alert_config,
      updatedAt: now,
    });
  }

  // 3. Centralized Machines Synchronization
  let hasEntityChanges = false;
  if (Array.isArray(config.machines) && config.machines.length > 0) {
    for (const m of config.machines) {
      if (m && m.id) {
        await db.machines.put({
          ...m,
          updatedAt: m.updatedAt || now,
        });
      }
    }
    hasEntityChanges = true;
  }

  // 4. Centralized Templates Synchronization
  if (Array.isArray(config.templates) && config.templates.length > 0) {
    for (const t of config.templates) {
      if (t && t.id) {
        await db.templates.put({
          ...t,
          updatedAt: t.updatedAt || now,
        });
      }
    }
    hasEntityChanges = true;
  }

  // 5. Centralized Inspectors Synchronization
  if (Array.isArray(config.inspectors) && config.inspectors.length > 0) {
    await saveSetting(INSPECTORS_SETTING_KEY, config.inspectors);
    hasEntityChanges = true;
  }

  // Record timestamp of synchronization
  await saveSetting('system_data_updated_at', now);

  if (hasEntityChanges) {
    notifySystemDataUpdates();
  }
}

/**
 * Fetches server configuration from /api/app-config or fallback to /app-config.json
 */
export async function fetchServerConfig(): Promise<CloudSystemConfig | null> {
  if (typeof fetch === 'undefined') return null;

  try {
    const apiResp = await fetch(`/api/app-config?t=${Date.now()}`, {
      method: 'GET',
      headers: { Accept: 'application/json' },
    });
    if (apiResp.ok) {
      const data = await apiResp.json();
      return data.config || data;
    }
  } catch (_) {}

  try {
    const staticResp = await fetch(`/app-config.json?t=${Date.now()}`, {
      method: 'GET',
      headers: { Accept: 'application/json' },
    });
    if (staticResp.ok) {
      return await staticResp.json();
    }
  } catch (_) {}

  return null;
}

/**
 * Fetches latest cloud configuration from Google Sheets Web App
 */
export async function fetchCloudConfig(
  webAppUrl: string
): Promise<{ success: boolean; config?: CloudSystemConfig; message?: string }> {
  if (!webAppUrl || !webAppUrl.startsWith('http')) {
    return { success: false, message: 'Invalid Google Apps Script Web App URL' };
  }

  const payload = {
    action: 'get_config',
    timestamp: new Date().toISOString(),
  };

  try {
    const resp = await fetch(webAppUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain;charset=utf-8' },
      body: JSON.stringify(payload),
    });

    if (!resp.ok) {
      return { success: false, message: `HTTP ${resp.status}: ${resp.statusText}` };
    }

    const data = await resp.json();
    if (data.success && data.config) {
      return { success: true, config: data.config };
    }

    return { success: false, message: data.message || 'Config not found in Google Sheets' };
  } catch (err: any) {
    return { success: false, message: err.message || 'Network error fetching cloud config' };
  }
}

/**
 * Broadcast entire system data (Machines, Templates, Inspectors, Settings)
 * to Server disk (/api/app-config) and Google Sheets (System_Config)
 */
export async function broadcastSystemData(options?: { reason?: string }): Promise<BroadcastResult> {
  const now = new Date().toISOString();

  // Read all current entities from local database
  const [machines, templates, inspectors, currentGasUrl, gasConfig, lineConfig] = await Promise.all([
    getAllMachines(),
    getAllTemplates(),
    getAllInspectors(),
    getSetting<string>('gas_endpoint_url'),
    getSetting<{ enabled?: boolean; webAppUrl?: string }>('gas_config'),
    getSetting<LineAlertConfig>('line_alert_config'),
  ]);

  const activeGasUrl = currentGasUrl || gasConfig?.webAppUrl || EMBEDDED_SYSTEM_CONFIG.gas_endpoint_url;

  const fullPayload: CloudSystemConfig = {
    gas_endpoint_url: activeGasUrl,
    gas_config: {
      key: 'gas_config',
      webAppUrl: activeGasUrl,
      enabled: gasConfig?.enabled !== false,
      autoSync: true,
      updatedAt: now,
    },
    line_alert_config: lineConfig || EMBEDDED_SYSTEM_CONFIG.line_alert_config,
    machines: machines || [],
    templates: templates || [],
    inspectors: inspectors || [],
    systemDataUpdatedAt: now,
    version: '1.2.0',
    updatedAt: now,
  };

  await saveSetting('system_data_updated_at', now);
  let localSaved = true;
  let serverSaved = false;
  let cloudSaved = false;

  // 1. Broadcast to Server Host (/api/app-config)
  try {
    const serverResp = await fetch('/api/app-config', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(fullPayload),
    });
    if (serverResp.ok) {
      serverSaved = true;
    }
  } catch (err) {
    console.warn('[cloudConfigService] Server broadcast skipped or not reachable:', err);
  }

  // 2. Broadcast to Google Sheets
  if (activeGasUrl && activeGasUrl.startsWith('http')) {
    try {
      const gasPayload = {
        action: 'save_config',
        config: fullPayload,
        timestamp: now,
      };

      const gasResp = await fetch(activeGasUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain;charset=utf-8' },
        body: JSON.stringify(gasPayload),
      });

      if (gasResp.ok) {
        const gasData = await gasResp.json();
        if (gasData.success) {
          cloudSaved = true;
        }
      }
    } catch (gasErr) {
      console.warn('[cloudConfigService] Google Sheets cloud config push error:', gasErr);
    }
  }

  const reasonMsg = options?.reason ? ` (${options.reason})` : '';
  let message = `บันทึกข้อมูลเรียบร้อยแล้ว${reasonMsg}`;
  if (serverSaved && cloudSaved) {
    message = `บันทึกและกระจายข้อมูลเครื่องจักร, แม่แบบ และรายชื่อช่าง ไปยังเครื่องแม่ข่ายและ Google Sheets เรียบร้อยแล้ว (ทุกเครื่องจะได้รับค่านี้อัตโนมัติ)${reasonMsg}`;
  } else if (serverSaved) {
    message = `บันทึกและกระจายข้อมูลไปยังเครื่องแม่ข่ายเรียบร้อยแล้ว (เครื่องในวงแลนจะได้รับค่านี้อัตโนมัติ)${reasonMsg}`;
  } else if (cloudSaved) {
    message = `บันทึกและกระจายข้อมูลไปยัง Google Sheets เรียบร้อยแล้ว (เครื่องลูกข่ายจะได้รับค่านี้อัตโนมัติ)${reasonMsg}`;
  }

  return {
    localSaved,
    serverSaved,
    cloudSaved,
    message,
  };
}

/**
 * Convenience alias for backward compatibility with settings components
 */
export async function saveAndBroadcastConfig(
  updatedConfig: {
    gas_endpoint_url?: string;
    gas_config?: {
      enabled?: boolean;
      webAppUrl?: string;
    };
    line_alert_config?: Partial<LineAlertConfig>;
  }
): Promise<BroadcastResult> {
  const now = new Date().toISOString();

  const currentGasUrl =
    updatedConfig.gas_endpoint_url ||
    updatedConfig.gas_config?.webAppUrl ||
    (await getSetting<string>('gas_endpoint_url')) ||
    EMBEDDED_SYSTEM_CONFIG.gas_endpoint_url;

  if (updatedConfig.gas_endpoint_url) {
    await saveSetting('gas_endpoint_url', currentGasUrl);
  }

  if (updatedConfig.gas_config) {
    await saveSetting('gas_config', {
      key: 'gas_config',
      webAppUrl: currentGasUrl,
      enabled: updatedConfig.gas_config.enabled !== false,
      autoSync: true,
      updatedAt: now,
    });
  }

  if (updatedConfig.line_alert_config) {
    await saveLineAlertConfig({
      enabled: updatedConfig.line_alert_config.enabled !== false,
      mode: updatedConfig.line_alert_config.mode || 'gas_relay',
      webhookUrl: updatedConfig.line_alert_config.webhookUrl || '',
      apiToken: updatedConfig.line_alert_config.apiToken || '',
      targetId: updatedConfig.line_alert_config.targetId || '',
      gasRelayUrl: updatedConfig.line_alert_config.gasRelayUrl || currentGasUrl,
      updatedAt: now,
    });
  }

  return await broadcastSystemData({ reason: 'Settings updated' });
}

/**
 * Generates a 1-Click setup URL containing current configurations and machines
 */
export async function generateShareableSetupUrl(): Promise<string> {
  if (typeof window === 'undefined') return '';

  const gasUrl = (await getSetting<string>('gas_endpoint_url')) || EMBEDDED_SYSTEM_CONFIG.gas_endpoint_url;
  const lineConfig = await getSetting<LineAlertConfig>('line_alert_config');

  const configObj: CloudSystemConfig = {
    gas_endpoint_url: gasUrl,
    gas_config: {
      webAppUrl: gasUrl,
      enabled: true,
      updatedAt: new Date().toISOString(),
    },
    line_alert_config: lineConfig,
    updatedAt: new Date().toISOString(),
  };

  const encoded = btoa(unescape(encodeURIComponent(JSON.stringify(configObj))));
  const origin = window.location.origin;
  const pathname = window.location.pathname;

  return `${origin}${pathname}?setup=true&config=${encoded}`;
}
